import {gql} from '@apollo/client';
import {FragmentDefinitionNode} from 'graphql';

import {assert} from 'Asserts';
import {ExcludeCondition} from 'graphql-client/declarative-translations/RESTExcludeConditions';
import translateListOrObject from 'graphql-client/declarative-translations/translateListOrObject';
import shallowCopyNote from 'notebook/notes/shallowCopyNote';

import BaseFieldTranslator from './BaseFieldTranslator';
import {getDeclarativeTranslationsMapping} from './DeclarativeTranslationsMapping';
import {buildDeclarativeTranslator, DeclarativeTranslator, TranslatableValue} from './DeclarativeTranslator';
import {PossibleTypesMapping} from './PossibleTypesMapping';
import {GraphQLSelectionSet, RESTField} from './preprocessedData';
import {getDefaultTypenameResolver} from './TypenameResolver';
import {RESTSelectionSetOptions} from './Types';

const FOLDER_FRAGMENT_DEFINITION = gql`
  fragment CustomFolderSummaryGraphQL on FolderNode {
    source {
      ...SourceSummaryWithoutRootFolderGraphQL
      rootFolder {
        ...FolderSummaryWithoutParentsWithoutSourceGraphQL
      }
    }
    ...FolderSummaryWithoutParentsWithoutSourceGraphQL
  }
`.definitions[0];

const FOLDER_REST_DECLARATION: RESTSelectionSetOptions = {
  fields: {
    source: {
      spreads: {SourceSummaryWithoutRootFolder: {}},
      fields: {
        rootFolder: {
          spreads: {FolderSummaryWithoutParentsWithoutSource: {}},
          typenameResolver: 'FolderTypenameResolver',
        },
      },
      typenameResolver: 'SourceTypenameResolver',
    },
  },
  spreads: {FolderSummaryWithoutParentsWithoutSource: {}},
  typenameResolver: 'FolderTypenameResolver',
};

class TestFooTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: TranslatableValue): unknown {
    return (restObj[this.restKey] as string).toUpperCase();
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    return (gqlObj[this.graphQLKey] as string).toLowerCase();
  }
}

// Note: this may end up getting merged into IdentityFieldTranslator if we continue to encounter use cases
// that justify adding to the complexity of that implementation
class IdentityWithUnionsFieldTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: TranslatableValue): unknown {
    const restValue = restObj[this.restKey];
    return translateListOrObject(restValue, (id: string) => ({
      __typename: getDefaultTypenameResolver().getTypenameFromID(id),
      id,
    }));
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    const gqlValue = gqlObj[this.graphQLKey];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return translateListOrObject(gqlValue, (value: any) => value.id ?? value.invalidId ?? null);
  }
}

class IdentityWithInaccessibleUnionsFieldTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: TranslatableValue): unknown {
    const inaccessibleIds = (
      restObj.hasOwnProperty('inaccessibleItemIds') ? restObj.inaccessibleItemIds : []
    ) as Array<string>;
    const restValue = restObj[this.restKey];
    return translateListOrObject(restValue, (id: string) =>
      !inaccessibleIds.includes(id)
        ? {
            __typename: getDefaultTypenameResolver().getTypenameFromID(id),
            id,
          }
        : {
            __typename: 'InaccessibleInfoNode',
            invalidId: id,
          }
    );
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    const gqlValue = gqlObj[this.graphQLKey];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return translateListOrObject(gqlValue, (value: any) => value.id ?? value.invalidId ?? null);
  }
}

class IdentityForInaccessibleInfoFieldTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: TranslatableValue): unknown {
    const restValue = restObj[this.restKey];
    return translateListOrObject(restValue, (id: string) => ({
      __typename: 'InaccessibleInfoNode',
      invalidId: id,
    }));
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    const gqlValue = gqlObj[this.graphQLKey];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return translateListOrObject(gqlValue, (value: any) => value.invalidId ?? null);
  }
}

class FolderParentTranslator extends BaseFieldTranslator {
  folderTranslator: DeclarativeTranslator;

  constructor(restKey: string, graphQLKey: string, excludeCondition: ExcludeCondition | null) {
    super(restKey, graphQLKey, excludeCondition);

    const declarativeTranslationsMapping = getDeclarativeTranslationsMapping();

    this.folderTranslator = buildDeclarativeTranslator(
      'Folder:parent',
      FOLDER_REST_DECLARATION,
      FOLDER_FRAGMENT_DEFINITION as FragmentDefinitionNode,
      declarativeTranslationsMapping,
      // PossibleTypesMapping isn't necessary in this instance, and it's a pain to thread it down
      new PossibleTypesMapping({
        ProjectNode: new Set(['ProjectNode', 'SourceInterface']),
        RegistryNode: new Set(['RegistryNode', 'SourceInterface']),
      })
    );
  }

  public getGQLValue(restObj: TranslatableValue): unknown {
    const toDictFolders = [];
    let currentFolder = restObj[this.restKey];

    while (currentFolder != null) {
      // @ts-expect-error
      toDictFolders.push(this.folderTranslator.restToGQL(currentFolder));
      // @ts-expect-error
      currentFolder = currentFolder.parent;
    }

    return toDictFolders;
  }

  private translateParentListToTree(translatedFolders: Array<TranslatableValue>): TranslatableValue | null {
    if (!translatedFolders.length) {
      return null;
    }

    translatedFolders[0].parent = this.translateParentListToTree(
      translatedFolders.slice(1, translatedFolders.length)
    );
    return translatedFolders[0];
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    const translatedFolders = (gqlObj[this.graphQLKey] as Array<unknown>).map((folder) =>
      this.folderTranslator.gqlToREST(folder as TranslatableValue)
    );
    for (const folder of translatedFolders) {
      if (folder.source != null) {
        // @ts-expect-error
        folder.source.rootFolder.parent = null;
      }
    }
    return this.translateParentListToTree(translatedFolders);
  }
}

// TODO: Consider statically typing this
/* eslint-disable @typescript-eslint/no-explicit-any */

class BioentityAliasInfosTranslator extends BaseFieldTranslator {
  private static graphQLBioentityAliasInfoToREST(graphQLBAI: any): unknown {
    return {
      alias: graphQLBAI.alias,
      registryCollectionOwner: graphQLBAI.registryCollectionOwnerJson,
    };
  }

  private static restBioentityAliasInfoToGraphQL(restBAI: any, bioentityId: string): unknown {
    return {
      __typename: 'BioentityAliasInfoNode',
      id: `${bioentityId}-${restBAI.alias}`,
      alias: restBAI.alias,
      registryCollectionOwnerJson: restBAI.registryCollectionOwner,
    };
  }

  public getGQLValue(restObj: any): unknown {
    return restObj[this.restKey].map((restBAI: any) =>
      BioentityAliasInfosTranslator.restBioentityAliasInfoToGraphQL(restBAI, restObj.id)
    );
  }

  public getRESTValue(gqlObj: any): unknown {
    return gqlObj[this.graphQLKey].map((gqlBAI: any) =>
      BioentityAliasInfosTranslator.graphQLBioentityAliasInfoToREST(gqlBAI)
    );
  }
}

class StorableBarcodeTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: any): {__typename: 'BarcodeNode'; value: string | null} {
    return {__typename: 'BarcodeNode', value: restObj.barcode};
  }

  public getRESTValue(gqlObj: any): string | null {
    return gqlObj.barcode?.value ?? null;
  }
}

class MutableNoteTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: any): unknown {
    return restObj;
  }

  public getRESTValue(gqlObj: any): unknown {
    const noteJson = gqlObj.noteJson;
    return shallowCopyNote(noteJson);
  }
}

class SequenceCreatorTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: any): {__typename: 'UserNode'; id: string; handle: string} {
    return {
      __typename: 'UserNode',
      id: restObj.creator_id,
      handle: restObj[this.restKey],
    };
  }

  public getRESTValue(gqlObj: any): string {
    return gqlObj[this.restKey].handle;
  }
}

class AssayRecordOriginFieldTranslater extends BaseFieldTranslator {
  public getGQLValue(restObj: any): {__typename: 'AssayRecordOriginNode'; type: string} {
    return {
      __typename: 'AssayRecordOriginNode',
      type: restObj[this.restKey],
    };
  }

  public getRESTValue(gqlObj: any): string {
    return gqlObj[this.graphQLKey].type;
  }
}

interface GQLParentJsonFields {
  parent: string;
  parentId: string;
  parentUrl: string;
  parentVersionId: number;
  parentVersionName: string;
}

class SequenceParentTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: any): GQLParentJsonFields | null {
    if (restObj.hasOwnProperty('parent')) {
      return {
        parent: restObj.parent,
        parentId: restObj.parent_id,
        parentUrl: restObj.parent_url,
        parentVersionId: restObj.parent_version_id,
        parentVersionName: restObj.parent_version_name,
      };
    }
    return null;
  }

  public getRESTValue(gqlObj: any): string | null {
    return gqlObj.parentJson?.parent ?? null;
  }
}

class RunSchemaIDTranslator extends BaseFieldTranslator {
  public getGQLValue(restObj: any): {__typename: 'RunSchemaNode'; id: string; name: string} {
    return {
      __typename: 'RunSchemaNode',
      id: restObj[this.restKey],
      name: restObj.schemaName,
    };
  }

  public getRESTValue(gqlObj: any): string {
    return gqlObj[this.graphQLKey].id;
  }
}

/* eslint-enable @typescript-eslint/no-explicit-any */
// Keep in sync with custom_field_translator.py
const CUSTOM_TRANSLATOR_CLS_BY_KEY: {[key: string]: typeof BaseFieldTranslator} = {
  'AnalyticsDashboardLink:originalAnalyticsDashboardId': IdentityWithUnionsFieldTranslator,
  'AssayRecordOriginNode:type': AssayRecordOriginFieldTranslater,
  'BaseFolderItemFile:note': MutableNoteTranslator,
  'Batch:migratedToBasicFolderItemId': IdentityWithUnionsFieldTranslator,
  'ContainerContent:containerId': IdentityWithUnionsFieldTranslator,
  'ContainerContent:containedItemId': IdentityWithUnionsFieldTranslator,
  'Folder:parent': FolderParentTranslator,
  'MixtureInstructions:note': MutableNoteTranslator,
  'RegistryItem:bioentityAliasInfos': BioentityAliasInfosTranslator,
  'Run:schemaId': RunSchemaIDTranslator,
  'Sequence:creator': SequenceCreatorTranslator,
  'Sequence:parent': SequenceParentTranslator,
  'Storable:barcode': StorableBarcodeTranslator,
  'Storable:parentStorableId': IdentityWithUnionsFieldTranslator,
  'Test:foo': TestFooTranslator,
  'Worklist:itemIds': IdentityWithInaccessibleUnionsFieldTranslator,
  'Worklist:archivedItemIds': IdentityWithInaccessibleUnionsFieldTranslator,
  'Worklist:inaccessibleItemIds': IdentityForInaccessibleInfoFieldTranslator,
};

/**
 * CustomFieldTranslator handles behavior that does not fit into any of the other categories.
 *
 * A common usecase is converting rest fields <-> graphql objects.The most common usecase of converting ID
 * fields is covered by IdentityFieldTranslator.
 *
 * Example:
 *    - x <-> {__typename: 'T', value: x}
 */
export default class CustomFieldTranslator extends BaseFieldTranslator {
  customFieldTranslator: BaseFieldTranslator;

  constructor(restKey: string, graphQLKey: string, customFieldTranslator: BaseFieldTranslator) {
    super(restKey, graphQLKey, customFieldTranslator.excludeCondition);
    this.customFieldTranslator = customFieldTranslator;
  }

  public static tryParse(
    declarationName: string,
    parsedRESTField: RESTField,
    graphQLSelectionSet: GraphQLSelectionSet,
    possibleTypesMapping: PossibleTypesMapping
  ): CustomFieldTranslator | null {
    if (parsedRESTField.customFieldTranslator) {
      const graphQLFieldName = parsedRESTField.customGraphQLName || parsedRESTField.fieldName;

      assert(
        CUSTOM_TRANSLATOR_CLS_BY_KEY[parsedRESTField.customFieldTranslator] != null,
        `Unexpected customFieldTranslator for field "${parsedRESTField.fieldName}": ${parsedRESTField.customFieldTranslator}`
      );

      return new CustomFieldTranslator(
        parsedRESTField.fieldName,
        graphQLFieldName,
        // @ts-expect-error TS doesn't understand that it's a mapping of key to concrete class, so it complains that
        // you can't instantiate an abstract class.
        new CUSTOM_TRANSLATOR_CLS_BY_KEY[parsedRESTField.customFieldTranslator](
          parsedRESTField.fieldName,
          graphQLFieldName,
          parsedRESTField.getExcludeCondition()
        )
      );
    }

    return null;
  }

  public getGQLValue(restObj: TranslatableValue): unknown {
    return this.customFieldTranslator.getGQLValue(restObj);
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    return this.customFieldTranslator.getRESTValue(gqlObj);
  }
}
